export enum CampaignType {
  email = 'email',
  socialMedia = 'social_media',
  content = 'content',
  event = 'event',
  webinar = 'webinar',
  paidAdvertising = 'paid_advertising',
  other = 'other',
}

export enum CampaignStatus {
  draft = 'draft',
  scheduled = 'scheduled',
  active = 'active',
  paused = 'paused',
  completed = 'completed',
  cancelled = 'cancelled',
}

export enum LeadSource {
  website = 'website',
  referral = 'referral',
  socialMedia = 'social_media',
  email = 'email',
  event = 'event',
  webinar = 'webinar',
  paidAdvertising = 'paid_advertising',
  content = 'content',
  coldCall = 'cold_call',
  other = 'other',
}

export enum EmailStatus {
  draft = 'draft',
  scheduled = 'scheduled',
  sending = 'sending',
  sent = 'sent',
  failed = 'failed',
  cancelled = 'cancelled',
}

export interface MarketingCampaign {
  id: string;
  name: string;
  type: CampaignType;
  status: CampaignStatus;
  description?: string;
  startDate?: Date;
  endDate?: Date;
  budget?: number;
  spent?: number;
  targetAudience?: string;
  goals?: string[];
  metrics?: CampaignMetrics;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface CampaignMetrics {
  impressions?: number;
  clicks?: number;
  opens?: number;
  conversions?: number;
  leads?: number;
  revenue?: number;
  costPerLead?: number;
  costPerAcquisition?: number;
  roi?: number;
  conversionRate?: number;
  clickThroughRate?: number;
  openRate?: number;
}

export interface EmailCampaign {
  id: string;
  campaignId?: string;
  name: string;
  subject: string;
  fromEmail: string;
  fromName: string;
  templateId?: string;
  htmlContent: string;
  textContent?: string;
  status: EmailStatus;
  scheduledAt?: Date;
  sentAt?: Date;
  recipientCount: number;
  deliveredCount?: number;
  openedCount?: number;
  clickedCount?: number;
  bouncedCount?: number;
  unsubscribedCount?: number;
  segments?: string[]; // User segment IDs
  tags?: string[];
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  category?: string;
  variables?: string[]; // Available template variables
  isActive: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface LeadSourceRecord {
  id: string;
  name: string;
  type: LeadSource;
  description?: string;
  campaignId?: string;
  url?: string;
  utmParameters?: Record<string, string>;
  isActive: boolean;
  createdAt: Date;
  lastUpdated: Date;
}

export interface ContentPost {
  id: string;
  title: string;
  type: 'blog' | 'social' | 'video' | 'podcast' | 'other';
  content?: string;
  url?: string;
  platform?: string; // e.g., "LinkedIn", "Twitter", "Blog"
  status: 'draft' | 'scheduled' | 'published' | 'archived';
  scheduledAt?: Date;
  publishedAt?: Date;
  author: string;
  authorName: string;
  tags?: string[];
  metrics?: {
    views?: number;
    likes?: number;
    shares?: number;
    comments?: number;
    clicks?: number;
  };
  createdAt: Date;
  lastUpdated: Date;
}

export interface Event {
  id: string;
  name: string;
  type: 'webinar' | 'conference' | 'workshop' | 'networking' | 'other';
  description?: string;
  startDate: Date;
  endDate: Date;
  location?: string;
  virtualUrl?: string;
  registrationUrl?: string;
  budget?: number;
  spent?: number;
  registrations?: number;
  attendees?: number;
  leads?: number;
  status: 'planned' | 'open' | 'in_progress' | 'completed' | 'cancelled';
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  lastUpdated: Date;
}

export interface MarketingAnalytics {
  period: {
    start: Date;
    end: Date;
  };
  totalCampaigns: number;
  totalSpent: number;
  totalLeads: number;
  totalConversions: number;
  totalRevenue: number;
  averageCostPerLead: number;
  averageCostPerAcquisition: number;
  averageROI: number;
  topPerformingCampaigns: Array<{
    campaignId: string;
    campaignName: string;
    metrics: CampaignMetrics;
  }>;
  leadSources: Array<{
    source: LeadSource;
    count: number;
    conversionRate: number;
  }>;
}

